import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, FormControlLabel, Switch, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from '@mui/material/Stack';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useCreateService } from '../../../hooks/services/useCreateService';
import { useUpdateService } from '../../../hooks/services/useUpdateService';

export default function ServiceForm({ successCallback = null, service = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(service ? service.name : "");
    const [description, setDescription] = React.useState(service ? service.description : "");
    const [price, setPrice] = React.useState(service ? service.price : 0);
    const [disabled, setDisabled] = React.useState(service ? service.disabled : false);

    const { create } = useCreateService(successCallback);
    const { update } = useUpdateService(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(service ? service.name : "");
        setDescription(service ? service.description : "");
        setPrice(service ? service.price : "");
        setDisabled(service ? service.disabled : false);
    }, [service, open]);

    const handleSave = async () => {
      const isValidCurrency = (value) => {
        // Matches numbers like "1", "1.23", ".9", ".50"
        return /^(?:\d+|\d*\.\d{1,2})$/.test(value);
      };

      if (!name.trim()) {
        showSnackbar(t('services.messages.name_required'), "warning");
        return;
      }
      if (!price.trim()) {
        showSnackbar(t('services.messages.price_required'), "warning");
        return;
      }
      if (!isValidCurrency(price)) {
        showSnackbar(t('services.messages.price_invalid'), "warning");
        return;
      }

      try {
          if (mode === "create") {
            await create({ 
              name: name,
              description: description,
              price: Number(price),
              disabled: disabled
            });
            showSnackbar(t('services.messages.create_success'), "success");
          } else if (mode === "edit" && service) {
            await update(service.id, { 
              name: name,
              description: description,
              price: Number(price),
              disabled: disabled
            });
            showSnackbar(t('services.messages.update_success'), "success");
          }
          setName("");
          setDescription("");
          setPrice("");
          setDisabled(false);
          onClose();     // close dialog
      } catch (err) {
        console.log("err.response", err.response);
        showSnackbar(t('services.messages.save_fail'), "error");
      }
    };

    const handleCancel = () => {
      setName("");
      setDescription("");
      setPrice("");
      setDisabled(false);
      onClose();
    };

    return (
      <>
      <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

      <DialogTitle>{mode === "create" ? "Add Service" : "Edit Service"}</DialogTitle>

      <DialogContent dividers>
          <Stack spacing={1}>
          <TextField
              label="Service Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
          />
          <TextField
              label="Description (optional)"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              rows={3}
              multiline
          />
          <TextField
            type="number"
            step="0.01"
            min="0"
            label="Price"
            variant="outlined"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            slotProps={{
              input: {
                inputMode: 'decimal',
                pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                endAdornment: (
                  <InputAdornment position="end">
                    {price && (
                      <IconButton
                        size="small"
                        onClick={() => setPrice('')}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
          />
          <FormControlLabel
              control={
                  <Switch
                  checked={disabled}
                  onChange={(e) => setDisabled(e.target.checked)}
                  color="primary"
                  />
              }
              label="Disabled"
          />
          </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}